import React from "react";

import { SortType } from "commons/types/api/common";
import { Column, OnHeaderCellClickInterface, SortDirection } from "commons/types/table";

export const isSelected = (
    sort: SortType | undefined,
    columnId: string,
    sortDirection: SortDirection
) => {
    return sort?.col === columnId && sort?.dir === sortDirection ? "selected" : null;
};

type PropsType<T> = {
    readonly columns: Array<Column<T>>;
    readonly sort?: SortType;
    readonly onHeaderCellClick?: OnHeaderCellClickInterface<T>;
};

function TableHeadCell<T>({
    column,
    sort,
    onHeaderCellClick,
}: {
    readonly column: Column<T>;
    readonly sort?: SortType;
    readonly onHeaderCellClick?: OnHeaderCellClickInterface<T>;
}) {
    const onTableHeadCellClick = React.useCallback(() => {
        if (column.orderable && onHeaderCellClick) {
            onHeaderCellClick(column);
        }
    }, [onHeaderCellClick, column]);

    const columnStyle: React.CSSProperties = {
        width: column.width ? column.width : "auto",
    };

    if (column.minWidth) {
        columnStyle.minWidth = column.minWidth;
    }

    if (column.headerAlign) {
        columnStyle.textAlign = column.headerAlign;
    }

    if (column.padding) {
        columnStyle.padding = column.padding;
    }

    if (column.thVerticalAlign) {
        columnStyle.verticalAlign = column.thVerticalAlign;
    }

    return (
        <th style={columnStyle} colSpan={column.thColSpan ?? 1}>
            <div className={"table-header"}>
                {column.orderable ? (
                    <button
                        type={"button"}
                        onClick={onTableHeadCellClick}
                        className={column.headerAlign}
                    >
                        <div className={"sort-container"}>
                            <div>
                                <div
                                    className={`arrow up ${isSelected(
                                        sort,
                                        column.id,
                                        SortDirection.Asc
                                    )}`}
                                />
                            </div>
                            <div>
                                <div
                                    className={`arrow down ${isSelected(
                                        sort,
                                        column.id,
                                        SortDirection.Desc
                                    )}`}
                                />
                            </div>
                        </div>

                        <span className={"name-container"}>{column.header}</span>
                    </button>
                ) : (
                    <span className={"name-container"}>{column.header}</span>
                )}
            </div>
        </th>
    );
}

function TableHead<T>({ columns, sort, onHeaderCellClick }: PropsType<T>) {
    let thColSpanIndex: number | null = null;

    return (
        <thead>
            <tr>
                {columns.map((column, i) => {
                    if (thColSpanIndex && i < thColSpanIndex) {
                        return null;
                    }

                    if (column.thColSpan) {
                        thColSpanIndex = column.thColSpan + i;
                    }

                    return (
                        <TableHeadCell
                            key={column.id}
                            column={column}
                            sort={sort}
                            onHeaderCellClick={onHeaderCellClick}
                        />
                    );
                })}
            </tr>
        </thead>
    );
}

export default TableHead;
